/**
 * Roadmap Stats Service
 * Tracks personalized user statistics for each roadmap:
 * - Study time per day and total
 * - View count and frequency
 * - Daily completion records (items completed per day)
 * 
 * NOW WITH: LocalStorage caching for offline access and 7-day auto-clearing
 */

import { supabase } from '@/config/supabase';
import { LocalStorageSyncService } from './localStorageSyncService';

export interface DailyRecord {
  date: string; // YYYY-MM-DD
  items_completed: number;
  duration_minutes: number;
  articles_studied: string[]; // Array of article IDs
  completed_at?: string;
}

export interface RoadmapStats {
  total_minutes_studied: number;
  times_viewed: number;
  daily_completion_records: Record<string, DailyRecord>;
  last_studied_at: string | null;
  last_accessed_at: string | null;
  current_streak: number;
  best_streak: number;
}

class RoadmapStatsService {
  /**
   * Track when user views/opens a roadmap
   * Increments times_viewed counter and updates last_accessed_at
   */
  static async trackRoadmapView(userEmail: string, roadmapId: string): Promise<void> {
    try {
      // Get current stats
      const { data: currentStats } = await supabase
        .from('user_roadmap_progress')
        .select('times_viewed')
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId)
        .single();

      const newViewCount = (currentStats?.times_viewed || 0) + 1;

      // Update view count and access time
      const { error } = await supabase
        .from('user_roadmap_progress')
        .update({
          times_viewed: newViewCount,
          last_accessed_at: new Date().toISOString()
        })
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId);

      if (error) throw error;
      console.log(`✅ Roadmap view tracked: ${roadmapId} (View #${newViewCount})`);
      
      // Save to localStorage for offline access
      LocalStorageSyncService.saveRoadmapProgress({
        roadmapId,
        status: 'in_progress',
        progressPercentage: 0,
        timesViewed: newViewCount,
        totalMinutesStudied: 0,
        lastAccessed: Date.now(),
        lastSynced: Date.now()
      });
    } catch (error) {
      console.error('❌ Error tracking roadmap view:', error);
    }
  }

  /**
   * Record study session for a roadmap
   * Updates total study time, daily records, and streak
   */
  static async recordStudySession(
    userEmail: string,
    roadmapId: string,
    durationMinutes: number,
    itemsCompleted: number = 0,
    articlesStudied: string[] = []
  ): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

      // Get current stats
      const { data: currentStats } = await supabase
        .from('user_roadmap_progress')
        .select('total_minutes_studied, daily_completion_records, last_studied_at')
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId)
        .maybeSingle();

      // If no record exists, create one
      if (!currentStats) {
        const { error: insertError } = await supabase
          .from('user_roadmap_progress')
          .insert({
            email: userEmail,
            roadmap_id: roadmapId,
            total_minutes_studied: durationMinutes,
            times_viewed: 1,
            status: 'in_progress'
          });
        
        if (insertError) {
          console.warn('Could not create progress record:', insertError);
          return;
        }
        return;
      }

      // Parse existing daily records
      const dailyRecords: Record<string, DailyRecord> = currentStats.daily_completion_records || {};

      // Update or create today's record
      if (dailyRecords[today]) {
        dailyRecords[today].duration_minutes += durationMinutes;
        dailyRecords[today].items_completed += itemsCompleted;
        dailyRecords[today].articles_studied = [
          ...new Set([...dailyRecords[today].articles_studied, ...articlesStudied])
        ];
      } else {
        dailyRecords[today] = {
          date: today,
          duration_minutes: durationMinutes,
          items_completed: itemsCompleted,
          articles_studied: articlesStudied,
          completed_at: new Date().toISOString()
        };
      }

      const newTotalMinutes = (currentStats.total_minutes_studied || 0) + durationMinutes;

      // Update database
      const { error } = await supabase
        .from('user_roadmap_progress')
        .update({
          total_minutes_studied: newTotalMinutes,
          daily_completion_records: dailyRecords,
          last_studied_at: new Date().toISOString()
        })
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId);

      if (error) throw error;
      console.log(`✅ Study session recorded: ${durationMinutes}min, total: ${newTotalMinutes}min`);
      
      // Save to localStorage for offline access
      LocalStorageSyncService.saveRoadmapProgress({
        roadmapId,
        status: 'in_progress',
        progressPercentage: 0,
        timesViewed: 1,
        totalMinutesStudied: newTotalMinutes,
        lastAccessed: Date.now(),
        lastSynced: Date.now()
      });
    } catch (error) {
      console.error('❌ Error recording study session:', error);
    }
  }

  /**
   * Get comprehensive stats for a roadmap
   */
  static async getRoadmapStats(userEmail: string, roadmapId: string): Promise<RoadmapStats | null> {
    try {
      const { data, error } = await supabase
        .from('user_roadmap_progress')
        .select(`
          total_minutes_studied,
          times_viewed,
          daily_completion_records,
          last_studied_at,
          last_accessed_at
        `)
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId)
        .maybeSingle(); // Use maybeSingle instead of single to allow 0 rows

      if (error) {
        console.warn('Could not fetch roadmap stats:', error);
        return null;
      }

      // If no data exists, return default stats
      if (!data) {
        return {
          total_minutes_studied: 0,
          times_viewed: 0,
          daily_completion_records: {},
          current_streak: 0,
          best_streak: 0,
          last_studied_at: null,
          last_accessed_at: null
        };
      }

      // Calculate streak from daily records
      const dailyRecords = data?.daily_completion_records || {};
      const { currentStreak, bestStreak } = this.calculateStreaks(Object.keys(dailyRecords));

      return {
        total_minutes_studied: data?.total_minutes_studied || 0,
        times_viewed: data?.times_viewed || 0,
        daily_completion_records: dailyRecords,
        last_studied_at: data?.last_studied_at || null,
        last_accessed_at: data?.last_accessed_at || null,
        current_streak: currentStreak,
        best_streak: bestStreak
      };
    } catch (error) {
      console.error('❌ Error fetching roadmap stats:', error);
      return null;
    }
  }

  /**
   * Get stats for all roadmaps of a user
   */
  static async getAllRoadmapStats(userEmail: string): Promise<Record<string, RoadmapStats>> {
    try {
      const { data, error } = await supabase
        .from('user_roadmap_progress')
        .select(`
          roadmap_id,
          total_minutes_studied,
          times_viewed,
          daily_completion_records,
          last_studied_at,
          last_accessed_at
        `)
        .eq('email', userEmail);

      if (error) throw error;

      const statsMap: Record<string, RoadmapStats> = {};
      
      data?.forEach(record => {
        const dailyRecords = record.daily_completion_records || {};
        const { currentStreak, bestStreak } = this.calculateStreaks(Object.keys(dailyRecords));

        statsMap[record.roadmap_id] = {
          total_minutes_studied: record.total_minutes_studied || 0,
          times_viewed: record.times_viewed || 0,
          daily_completion_records: dailyRecords,
          last_studied_at: record.last_studied_at || null,
          last_accessed_at: record.last_accessed_at || null,
          current_streak: currentStreak,
          best_streak: bestStreak
        };
      });

      return statsMap;
    } catch (error) {
      console.error('❌ Error fetching all roadmap stats:', error);
      return {};
    }
  }

  /**
   * Get daily completion record for a specific date
   */
  static async getDailyRecord(
    userEmail: string,
    roadmapId: string,
    date: string // YYYY-MM-DD
  ): Promise<DailyRecord | null> {
    try {
      const { data } = await supabase
        .from('user_roadmap_progress')
        .select('daily_completion_records')
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId)
        .single();

      const dailyRecords = data?.daily_completion_records || {};
      return dailyRecords[date] || null;
    } catch (error) {
      console.error('❌ Error fetching daily record:', error);
      return null;
    }
  }

  /**
   * Calculate current and best study streaks from daily records
   * A streak is consecutive days with study activity
   */
  private static calculateStreaks(dates: string[]): { currentStreak: number; bestStreak: number } {
    if (dates.length === 0) return { currentStreak: 0, bestStreak: 0 };

    // Sort dates in descending order (newest first)
    const sortedDates = dates.sort().reverse();
    const dateObjects = sortedDates.map(d => new Date(d));

    let currentStreak = 0;
    let bestStreak = 0;
    let tempStreak = 0;

    // Check if today or yesterday has activity
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let isCurrentStreakActive = false;
    if (dateObjects[0]) {
      const lastStudyDate = new Date(dateObjects[0]);
      lastStudyDate.setHours(0, 0, 0, 0);
      
      isCurrentStreakActive = lastStudyDate.getTime() === today.getTime() || 
                              lastStudyDate.getTime() === yesterday.getTime();
    }

    // Calculate streaks
    for (let i = 0; i < dateObjects.length; i++) {
      if (i === 0) {
        tempStreak = 1;
      } else {
        const current = new Date(dateObjects[i]);
        const previous = new Date(dateObjects[i - 1]);
        current.setHours(0, 0, 0, 0);
        previous.setHours(0, 0, 0, 0);

        const diffTime = previous.getTime() - current.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays === 1) {
          tempStreak++;
        } else {
          bestStreak = Math.max(bestStreak, tempStreak);
          tempStreak = 1;
        }
      }
    }

    bestStreak = Math.max(bestStreak, tempStreak);
    currentStreak = isCurrentStreakActive ? tempStreak : 0;

    return { currentStreak, bestStreak };
  }

  /**
   * Reset daily stats (admin function)
   */
  static async resetStats(userEmail: string, roadmapId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_roadmap_progress')
        .update({
          total_minutes_studied: 0,
          times_viewed: 0,
          daily_completion_records: {},
          last_studied_at: null
        })
        .eq('email', userEmail)
        .eq('roadmap_id', roadmapId);

      if (error) throw error;
      console.log('✅ Roadmap stats reset');
    } catch (error) {
      console.error('❌ Error resetting stats:', error);
    }
  }

  /**
   * Format minutes to readable time (e.g., "2h 15m")
   */
  static formatTime(minutes: number): string {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  /**
   * Get this week's total study time
   */
  static getWeeklyStudyTime(dailyRecords: Record<string, DailyRecord>): number {
    const today = new Date();
    let totalMinutes = 0;

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      if (dailyRecords[dateStr]) {
        totalMinutes += dailyRecords[dateStr].duration_minutes;
      }
    }

    return totalMinutes;
  }

  /**
   * Get this month's total study time
   */
  static getMonthlyStudyTime(dailyRecords: Record<string, DailyRecord>): number {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    let totalMinutes = 0;

    Object.values(dailyRecords).forEach(record => {
      const recordDate = new Date(record.date);
      if (recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear) {
        totalMinutes += record.duration_minutes;
      }
    });

    return totalMinutes;
  }
}

export default RoadmapStatsService;
